import { t } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { DataQualityGrade, DataQualityScore } from '@lune-climate/lune'
import { AccordionTile, Text, Tooltip } from '@lune-fe/lune-ui-lib'
import { Box } from '@mui/material'
import { alpha } from '@mui/material/styles'

import {
    DataQualityScoreWithLabel,
    getDataQualityScoreColor,
    getDataQualityScoreFormattedLetter,
    getDataQualityScoreLabel,
} from '../dataQuality'

export const DataQualityScoreSection = ({
    dataQualityScore,
    legIndex,
    pdfStructure,
    title = t`Data quality score`,
    smallTitle = false,
}: {
    dataQualityScore: DataQualityScore
    legIndex?: string
    pdfStructure?: boolean
    title?: string
    smallTitle?: boolean
}) => {
    useLingui()

    const ef = dataQualityScore.components.emissionFactorScore
    const route = dataQualityScore.components.routeScore
    const load = dataQualityScore.components.shipmentScore

    return (
        <Box>
            <Text variant={smallTitle ? 'body2-medium' : 'h6'} sx={{ mb: 4, display: 'block' }}>
                {title}
            </Text>
            <AccordionTile
                expanded={pdfStructure}
                sx={{
                    '.MuiAccordionDetails-root': {
                        m: 0,
                    },
                    '.MuiAccordion-heading': {
                        backgroundColor: `Grey50`,
                        width: '100%',
                        borderRadius: '16px',
                        pl: 1,
                        pr: 1,
                        height: '88px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        '> div': {
                            width: '100%',
                        },
                    },
                    ...(pdfStructure && {
                        '.MuiAccordionSummary-expandIconWrapper': {
                            display: 'none',
                        },
                    }),
                }}
                title={<DataQualityScoreWithLabel score={dataQualityScore.score} />}
            >
                <Text
                    variant={'body3'}
                    sx={{
                        mt: 1 / 2,
                        display: 'inline-block',
                        color: `Grey600`,
                    }}
                >
                    {legIndex
                        ? t`This score reflects how accurate the CO₂ estimate is for Leg ${legIndex}. It is derived from the quality of input data, and any modelling Lune was able to do to improve the accuracy of the calculation.`
                        : t`This score reflects how accurate the CO₂ estimate is. It is derived from the quality of input data, and any modelling Lune was able to do to improve the accuracy of the calculation.`}
                </Text>
                <Box
                    sx={{
                        display: 'flex',
                        width: '100%',
                        flexDirection: 'column',
                        gap: 3,
                        mt: 4,
                        pl: 1,
                        pr: 1,
                    }}
                >
                    <Box
                        key={'emission-factor-score'}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            gap: 3,
                        }}
                    >
                        <ColoredCircle score={ef} />
                        <Box>
                            <Text variant={'body2'} sx={{ fontWeight: 700, mb: 1 / 2 }}>
                                {t`Emission Factor`}
                            </Text>
                            <Text variant={'body3'}>
                                {getDataQualityScoreFormattedLetter(ef)}:{' '}
                                {getDataQualityScoreLabel(ef)}
                            </Text>
                        </Box>
                    </Box>
                    {route !== null && (
                        <Box
                            key={'route-score'}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                gap: 3,
                            }}
                        >
                            <ColoredCircle score={route} />
                            <Box>
                                <Text variant={'body2'} sx={{ fontWeight: 700, mb: 1 / 2 }}>
                                    {t`Route`}
                                </Text>
                                <Text variant={'body3'}>
                                    {getDataQualityScoreFormattedLetter(route)}:{' '}
                                    {getDataQualityScoreLabel(route)}
                                </Text>
                            </Box>
                        </Box>
                    )}
                    <Box
                        key={'load-score'}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            gap: 3,
                        }}
                    >
                        <ColoredCircle score={load} />
                        <Box>
                            <Text variant={'body2'} sx={{ fontWeight: 700, mb: 1 / 2 }}>
                                {t`Load`}
                            </Text>
                            <Text variant={'body3'}>
                                {getDataQualityScoreFormattedLetter(load)}:{' '}
                                {getDataQualityScoreLabel(load)}
                            </Text>
                        </Box>
                    </Box>
                </Box>
            </AccordionTile>
        </Box>
    )
}

const ColoredCircle = ({ score }: { score: DataQualityGrade }) => {
    return (
        <Box
            sx={{
                width: '12px',
                height: '12px',
                backgroundColor: getDataQualityScoreColor(score),
                borderRadius: '100%',
            }}
        />
    )
}

export const TotalDataQualityScore = ({ score }: { score: DataQualityGrade }) => {
    const color = getDataQualityScoreColor(score)
    return (
        <Box>
            <Tooltip
                title={t`The Data Quality Score is an indicator of the overall accuracy of this estimate. It is a weighted average of all legs in the journey.`}
                placement="top"
                contentWidth={true}
            >
                <Box
                    sx={{
                        display: 'flex',
                        gap: 1,
                        alignItems: 'center',
                        py: 1 / 2,
                        px: 1,
                        borderRadius: '8px',
                        backgroundColor: alpha(color, 0.2),
                        border: `1px solid ${color}`,
                        width: 'max-content',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 1,
                            alignItems: 'center',
                        }}
                    >
                        <ColoredCircle score={score} />
                        <Text variant={'body3'}>{getDataQualityScoreFormattedLetter(score)}</Text>
                    </Box>

                    <Text variant={'body3'}>
                        {t`${getDataQualityScoreLabel(score)} data quality`}
                    </Text>
                </Box>
            </Tooltip>
        </Box>
    )
}

export default DataQualityScoreSection
